import { useEffect, useRef, useState } from 'react';
import { OnboardingPopupStepsEnum } from '../components/onboarding/OnboardingPopupSteps';
import { useAppDispatch } from '../hooks/store-hook';
import { lightBoxActions } from '../store/lightBoxSlice';
import Onboarding from '../components/onboarding/Onboarding';
import FlashMissionsPopup from '../components/common/mission/FlashMissionsPopup';
import MissionReferralPopup from '../components/onboarding/MissionReferralPopup';
import { missionsActions } from '../store/missionsSlice';
import { MapLocation } from '../models/MapModels';
import { MysteryShopperAddressType } from '../api/api.g';
import { appFlowActions } from '../store/appFlowSlice';
import { LoggedState } from '../models/AuthModelsCommon';

const FLASH_MISSIONS_ONBOARDING_CODE = 'FlashMissionsOnboarding';
let onboardingCompletedInSession = false;

interface UseOnboardingFlowsParams {
  authLoggedState: any;
  userPrefs: any;
  addressManager: any;
  profileManagerRef: any;
  advertisementTracking: any;
  currentLocation?: MapLocation | null;
  missionReferralPopupPending?: boolean;
}

interface UseOnboardingFlowsResult {
  onboardingPopupSteps: OnboardingPopupStepsEnum;
  setOnboardingPopupSteps: (step: OnboardingPopupStepsEnum) => void;
}

export const useOnboardingFlows = ({
  authLoggedState,
  userPrefs,
  addressManager,
  profileManagerRef,
  advertisementTracking,
  currentLocation,
  missionReferralPopupPending,
}: UseOnboardingFlowsParams): UseOnboardingFlowsResult => {
  const dispatch = useAppDispatch();
  const [onboardingPopupSteps, setOnboardingPopupSteps] = useState<OnboardingPopupStepsEnum>(OnboardingPopupStepsEnum.Initial);
  const lastCompletedStepAddressCountRef = useRef<number | undefined>(undefined);
  const processedStepRef = useRef<OnboardingPopupStepsEnum | undefined>(undefined);
  const missionReferralPopupConsumedRef = useRef(false);
  const hasPendingMissionReferralPopup = !!missionReferralPopupPending && !missionReferralPopupConsumedRef.current;

  // Reset onboarding when user is not logged.
  useEffect(() => {
    if (authLoggedState == null) {
      return;
    }
    if (authLoggedState === LoggedState.NotLogged) {
      onboardingCompletedInSession = false;
      missionReferralPopupConsumedRef.current = false;
      processedStepRef.current = undefined;
      lastCompletedStepAddressCountRef.current = undefined;
      setOnboardingPopupSteps(OnboardingPopupStepsEnum.Initial);
    }
  }, [authLoggedState]);

  // Start onboarding once advertisements are loaded.
  useEffect(() => {
    if (!advertisementTracking?.loaded) {
      return;
    }

    if (onboardingCompletedInSession) {
      processedStepRef.current = undefined;
      setOnboardingPopupSteps((currentStep) =>
        currentStep === OnboardingPopupStepsEnum.EmailVerified
          ? currentStep
          : hasPendingMissionReferralPopup
            ? OnboardingPopupStepsEnum.MissionReferral
            : OnboardingPopupStepsEnum.Completed
      );
      return;
    }

    setOnboardingPopupSteps((currentStep) => (currentStep === OnboardingPopupStepsEnum.Initial ? OnboardingPopupStepsEnum.Onboarding : currentStep));
  }, [advertisementTracking?.loaded, hasPendingMissionReferralPopup]);

  useEffect(() => {
    if (onboardingPopupSteps === OnboardingPopupStepsEnum.Completed) {
      onboardingCompletedInSession = true;
    }
  }, [onboardingPopupSteps]);

  useEffect(() => {
    if (onboardingPopupSteps !== OnboardingPopupStepsEnum.Completed || !hasPendingMissionReferralPopup) {
      return;
    }

    processedStepRef.current = undefined;
    setOnboardingPopupSteps(OnboardingPopupStepsEnum.MissionReferral);
  }, [hasPendingMissionReferralPopup, onboardingPopupSteps]);

  // Core onboarding step state machine (except location-permission request, which is coordinated with location hook).
  useEffect(() => {
    switch (onboardingPopupSteps) {
      case OnboardingPopupStepsEnum.Onboarding:
        if (processedStepRef.current === onboardingPopupSteps) {
          return;
        }
        processedStepRef.current = onboardingPopupSteps;

        if (advertisementTracking.showAdvertisement('Onboarding')) {
          const handleOnBoardingClose = (dontShowAgain: boolean) => {
            if (dontShowAgain) {
              advertisementTracking.dontShowAgain('Onboarding');
            }
            processedStepRef.current = undefined;
            setOnboardingPopupSteps(OnboardingPopupStepsEnum.LocationPermissionRequest);
          };

          dispatch(lightBoxActions.setContent(<Onboarding onClose={handleOnBoardingClose} />));
          dispatch(lightBoxActions.setVisibility(true));
        } else {
          processedStepRef.current = undefined;
          setOnboardingPopupSteps(OnboardingPopupStepsEnum.LocationPermissionRequest);
        }
        break;

      case OnboardingPopupStepsEnum.AddressRequest:
        if (userPrefs.profiles === undefined) {
          return;
        }
        if (processedStepRef.current === onboardingPopupSteps) {
          return;
        }
        processedStepRef.current = onboardingPopupSteps;

        {
          const dateOfBirth = userPrefs.profiles?.find((x: any) => x.key === 'BirthDate');
          addressManager.showAddressRequestPopup(() => {
            processedStepRef.current = undefined;
            if (!dateOfBirth?.value) {
              setOnboardingPopupSteps(OnboardingPopupStepsEnum.AgeVerification);
            } else {
              setOnboardingPopupSteps(OnboardingPopupStepsEnum.FlashMissionsOnboarding);
            }
          });
        }
        break;

      case OnboardingPopupStepsEnum.OnARoll:
        processedStepRef.current = undefined;
        dispatch(missionsActions.setShowOnARollPopup(false));
        break;

      case OnboardingPopupStepsEnum.AgeVerification: {
        if (processedStepRef.current === onboardingPopupSteps) {
          return;
        }
        const dateOfBirthMember = userPrefs.profileMembers.find((m: any) => m.key === 'BirthDate');
        if (!profileManagerRef.current?.showProfileMemberPopup) {
          return;
        }

        processedStepRef.current = onboardingPopupSteps;
        profileManagerRef.current.showProfileMemberPopup({ profileMember: dateOfBirthMember, value: undefined, hideCloseButton: true }, () => {
          processedStepRef.current = undefined;
          setOnboardingPopupSteps(OnboardingPopupStepsEnum.FlashMissionsOnboarding);
        });
        break;
      }

      case OnboardingPopupStepsEnum.FlashMissionsOnboarding:
        if (advertisementTracking.showAdvertisement(FLASH_MISSIONS_ONBOARDING_CODE)) {
          if (processedStepRef.current === onboardingPopupSteps) {
            return;
          }
          processedStepRef.current = onboardingPopupSteps;

          const handleFlashMissionsOnboardingClose = (dontShowAgain?: boolean) => {
            if (dontShowAgain) {
              advertisementTracking.dontShowAgain(FLASH_MISSIONS_ONBOARDING_CODE);
            }
            processedStepRef.current = undefined;
            setOnboardingPopupSteps(hasPendingMissionReferralPopup ? OnboardingPopupStepsEnum.MissionReferral : OnboardingPopupStepsEnum.Completed);
          };

          dispatch(
            lightBoxActions.setContent(
              <FlashMissionsPopup onClose={handleFlashMissionsOnboardingClose} onViewFlashMissions={handleFlashMissionsOnboardingClose} />
            )
          );
          dispatch(lightBoxActions.setVisibility(true));
        } else {
          processedStepRef.current = undefined;
          setOnboardingPopupSteps(hasPendingMissionReferralPopup ? OnboardingPopupStepsEnum.MissionReferral : OnboardingPopupStepsEnum.Completed);
        }
        break;

      case OnboardingPopupStepsEnum.MissionReferral:
        if (!hasPendingMissionReferralPopup) {
          processedStepRef.current = undefined;
          setOnboardingPopupSteps(OnboardingPopupStepsEnum.Completed);
          return;
        }
        if (processedStepRef.current === onboardingPopupSteps) {
          return;
        }
        processedStepRef.current = onboardingPopupSteps;

        {
          const handleMissionReferralPopupClose = () => {
            missionReferralPopupConsumedRef.current = true;
            processedStepRef.current = undefined;
            setOnboardingPopupSteps(OnboardingPopupStepsEnum.Completed);
          };

          dispatch(
            lightBoxActions.setContent(<MissionReferralPopup onClose={handleMissionReferralPopupClose} onViewMission={handleMissionReferralPopupClose} />)
          );
          dispatch(lightBoxActions.setVisibility(true));
        }
        break;

      default:
        processedStepRef.current = undefined;
        break;
    }
  }, [
    onboardingPopupSteps,
    advertisementTracking,
    userPrefs,
    addressManager,
    profileManagerRef,
    dispatch,
    hasPendingMissionReferralPopup,
  ]);

  // Email-verified popup.
  useEffect(() => {
    dispatch(appFlowActions.setNotificationPromptEligible(onboardingPopupSteps === OnboardingPopupStepsEnum.Completed));
  }, [dispatch, onboardingPopupSteps]);

  useEffect(() => {
    if (userPrefs?.showEmailVerifiedPopup) {
      processedStepRef.current = undefined;
      setOnboardingPopupSteps(OnboardingPopupStepsEnum.EmailVerified);
    }
  }, [userPrefs?.showEmailVerifiedPopup]);

  // Profile information / address onboarding step.
  useEffect(() => {
    if (onboardingPopupSteps !== OnboardingPopupStepsEnum.ProfileInformationRequest) {
      return;
    }

    if (authLoggedState === LoggedState.Anonymous) {
      processedStepRef.current = undefined;
      setOnboardingPopupSteps(OnboardingPopupStepsEnum.FlashMissionsOnboarding);
      return;
    }

    if (userPrefs.profiles === undefined || userPrefs.addresses === undefined) {
      return;
    }

    const dateOfBirth = userPrefs.profiles?.find((x: any) => x.key === 'BirthDate');
    const hasOnlyCurrentOrNoAddress =
      (userPrefs?.addresses?.length > 0 && userPrefs?.addresses?.filter((x: any) => x.type != MysteryShopperAddressType.Current).length === 0) ||
      userPrefs?.addresses?.length == 0;
    const hasAddress = userPrefs?.addresses?.length > 0 && userPrefs?.addresses?.filter((x: any) => x.type != MysteryShopperAddressType.Current).length > 0;

    // Mirror existing CustomMap behavior: wait for a resolved currentLocation
    // when the user only has a "Current" address, so we can seed from it.
    if (!currentLocation && hasOnlyCurrentOrNoAddress) {
      return;
    }

    processedStepRef.current = undefined;
    if (currentLocation && hasOnlyCurrentOrNoAddress) {
      setOnboardingPopupSteps(OnboardingPopupStepsEnum.AddressRequest);
    } else if (hasAddress) {
      if (!dateOfBirth?.value) {
        setOnboardingPopupSteps(OnboardingPopupStepsEnum.AgeVerification);
      } else {
        setOnboardingPopupSteps(OnboardingPopupStepsEnum.FlashMissionsOnboarding);
      }
    } else {
      setOnboardingPopupSteps(OnboardingPopupStepsEnum.FlashMissionsOnboarding);
    }
  }, [authLoggedState, userPrefs.profiles, userPrefs?.addresses, onboardingPopupSteps, currentLocation]);

  // Enforce required-address popup again after onboarding is completed
  // (for example, if the user deletes their only saved address).
  useEffect(() => {
    if (userPrefs?.addresses === undefined) {
      return;
    }

    if (onboardingPopupSteps !== OnboardingPopupStepsEnum.Completed) {
      return;
    }

    const nonCurrentAddressCount = userPrefs.addresses.filter((x: any) => x.type != MysteryShopperAddressType.Current).length;
    const previousNonCurrentAddressCount = lastCompletedStepAddressCountRef.current;
    lastCompletedStepAddressCountRef.current = nonCurrentAddressCount;

    if (!currentLocation || nonCurrentAddressCount > 0) {
      return;
    }
    const deletedLastAddress = previousNonCurrentAddressCount > 0;
    if (deletedLastAddress) {
      processedStepRef.current = undefined;
      setOnboardingPopupSteps(OnboardingPopupStepsEnum.AddressRequest);
    }
  }, [userPrefs?.addresses, onboardingPopupSteps, currentLocation]);

  return {
    onboardingPopupSteps,
    setOnboardingPopupSteps,
  };
};

export default useOnboardingFlows;
